/**
 * A small, accessible "?" affordance that reveals a short help popover.
 *
 * Opens on hover/focus (mouse + keyboard) and toggles on click; closes on Escape, blur, or an
 * outside click. The trigger is a real <button> with aria-expanded + an aria-describedby link to the
 * popover so screen readers announce the help text. Uses existing CSS tokens — no extra dependency.
 *
 * The popover is a TRUE floating overlay: `position: fixed` (see `.help-tip__pop` in global.css) with
 * its top/left computed here from the trigger's `getBoundingClientRect()`, so it floats over the page
 * instead of being clipped by — or expanding the scroll of — whatever container holds the trigger.
 * The position is viewport-edge clamped: it flips ABOVE the trigger if it would overflow the bottom,
 * and shifts left if it would overflow the right.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

interface HelpTipProps {
  /** The help content (string or rich nodes). */
  children: ReactNode;
  /** Accessible label for the trigger; defaults to "Help". */
  label?: string;
  /** Anchor the popover to the right edge of the trigger (use near the page right side). */
  align?: "left" | "right";
}

/** Gap between the trigger and the popover, and the minimum margin we keep from the viewport edges. */
const GAP = 6;
const EDGE = 8;

export function HelpTip({ children, label = "Help", align = "left" }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);
  const popId = useId();

  // Position the fixed popover from the trigger rect, clamped to the viewport. Runs in a layout effect
  // so it's measured + placed before paint (no first-frame flash at 0,0). Re-clamps after we know the
  // popover's own size (a second pass once popRef has laid out).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const tr = trigger.getBoundingClientRect();
      const pop = popRef.current?.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const popW = pop?.width ?? 280;
      const popH = pop?.height ?? 0;

      // Horizontal: left-aligned to the trigger by default, right-aligned when asked; then clamp so
      // the popover never overflows either viewport edge.
      let left = align === "right" ? tr.right - popW : tr.left;
      left = Math.min(left, vw - popW - EDGE);
      left = Math.max(EDGE, left);

      // Vertical: below the trigger; flip above if it would overflow the bottom (and there's room up).
      let top = tr.bottom + GAP;
      if (popH > 0 && top + popH > vh - EDGE && tr.top - GAP - popH > EDGE) {
        top = tr.top - GAP - popH;
      }
      setPos({ top, left });
    };
    place();
    // A second pass on the next frame, once the popover has rendered and we can measure its real size.
    const raf = requestAnimationFrame(place);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, align]);

  // Close on Escape, and on a click/focus outside the widget.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onOutside = (e: Event) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("focusin", onOutside);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("focusin", onOutside);
    };
  }, [open]);

  return (
    <span
      className="help-tip"
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        ref={triggerRef}
        className="help-tip__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? popId : undefined}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
      >
        ?
      </button>
      {open && (
        <span
          id={popId}
          ref={popRef}
          role="tooltip"
          className={`help-tip__pop ${align === "right" ? "help-tip__pop--right" : ""}`.trim()}
          style={pos ? { top: pos.top, left: pos.left } : undefined}
        >
          {children}
        </span>
      )}
    </span>
  );
}
